import { z } from 'zod';

import { ALL_SKILL_IDS } from '../constants/skills.js';

import { defineEnvelope } from './envelope.js';
import { Vector2Schema } from './movement.js';

/**
 * Contratos de combate del MVP-0 (TKT-021).
 *
 * Tres familias:
 *   1) Intents (cliente → server): BasicAttack, SkillCast, Dodge.
 *   2) Outcomes (server → cliente concreto, dirigidos): DamageResolved,
 *      MomentumChanged, HpChanged, DodgeResolved, SkillCastResolved.
 *   3) Broadcasts (server → todos): TelegraphStarted, TelegraphResolved,
 *      EntityRemoved.
 *
 * Reglas:
 *   - skillId solo del enum cerrado (no string libre).
 *   - Cada outcome lleva tickId para correlación.
 *   - amount, value, delta enteros (no fraccionarios en wire para evitar drift).
 */

const SkillIdSchema = z.enum(ALL_SKILL_IDS as readonly [string, ...string[]]);

// ============ INTENTS ============

export const BasicAttackIntentPayloadSchema = z
  .object({
    inputSeq: z.number().int().nonnegative(),
    targetHint: z.string().uuid().optional(),
  })
  .strict();

export const BasicAttackIntentTag = 'player.basic_attack.intent' as const;
export const BasicAttackIntentEnvelopeSchema = defineEnvelope(
  BasicAttackIntentTag,
  BasicAttackIntentPayloadSchema,
);
export type BasicAttackIntentPayload = z.infer<typeof BasicAttackIntentPayloadSchema>;

export const SkillCastIntentPayloadSchema = z
  .object({
    inputSeq: z.number().int().nonnegative(),
    skillId: SkillIdSchema,
    targetHint: z.string().uuid().optional(),
  })
  .strict();

export const SkillCastIntentTag = 'player.skill_cast.intent' as const;
export const SkillCastIntentEnvelopeSchema = defineEnvelope(
  SkillCastIntentTag,
  SkillCastIntentPayloadSchema,
);
export type SkillCastIntentPayload = z.infer<typeof SkillCastIntentPayloadSchema>;

export const DodgeIntentPayloadSchema = z
  .object({
    inputSeq: z.number().int().nonnegative(),
    /** dir.magnitude ≤ 1 (servidor normaliza si excede). */
    dir: Vector2Schema,
  })
  .strict();

export const DodgeIntentTag = 'player.dodge.intent' as const;
export const DodgeIntentEnvelopeSchema = defineEnvelope(DodgeIntentTag, DodgeIntentPayloadSchema);
export type DodgeIntentPayload = z.infer<typeof DodgeIntentPayloadSchema>;

// ============ OUTCOMES (directed) ============

export const DamageResolvedPayloadSchema = z
  .object({
    attackerId: z.string(),
    targetId: z.string(),
    amount: z.number().int().nonnegative(),
    isCrit: z.boolean(),
    momentumGenerated: z.number().int().nonnegative().optional(),
    tickId: z.number().int().nonnegative(),
  })
  .strict();

export const DamageResolvedTag = 'combat.damage.resolved' as const;
export const DamageResolvedEnvelopeSchema = defineEnvelope(
  DamageResolvedTag,
  DamageResolvedPayloadSchema,
);
export type DamageResolvedPayload = z.infer<typeof DamageResolvedPayloadSchema>;

export const HpChangedPayloadSchema = z
  .object({
    entityId: z.string(),
    value: z.number().int().nonnegative(),
    delta: z.number().int(),
    tickId: z.number().int().nonnegative(),
  })
  .strict();

export const HpChangedTag = 'combat.hp.changed' as const;
export const HpChangedEnvelopeSchema = defineEnvelope(HpChangedTag, HpChangedPayloadSchema);
export type HpChangedPayload = z.infer<typeof HpChangedPayloadSchema>;

export const MomentumChangeReason = z.enum(['attack_hit', 'skill_cast', 'decay', 'reset']);

export const MomentumChangedPayloadSchema = z
  .object({
    playerSeatId: z.string().uuid(),
    value: z.number().int().nonnegative(),
    delta: z.number().int(),
    reason: MomentumChangeReason,
    tickId: z.number().int().nonnegative(),
  })
  .strict();

export const MomentumChangedTag = 'combat.momentum.changed' as const;
export const MomentumChangedEnvelopeSchema = defineEnvelope(
  MomentumChangedTag,
  MomentumChangedPayloadSchema,
);
export type MomentumChangedPayload = z.infer<typeof MomentumChangedPayloadSchema>;

export const DodgeResolvedPayloadSchema = z
  .object({
    playerSeatId: z.string().uuid(),
    success: z.boolean(),
    /** Tick en el que terminan los i-frames; antes de él el daño se anula. */
    iframesEndTick: z.number().int().nonnegative(),
    tickId: z.number().int().nonnegative(),
  })
  .strict();

export const DodgeResolvedTag = 'combat.dodge.resolved' as const;
export const DodgeResolvedEnvelopeSchema = defineEnvelope(
  DodgeResolvedTag,
  DodgeResolvedPayloadSchema,
);
export type DodgeResolvedPayload = z.infer<typeof DodgeResolvedPayloadSchema>;

export const SkillCastRejectReason = z.enum([
  'COOLDOWN',
  'INSUFFICIENT_MOMENTUM',
  'INSUFFICIENT_HP',
  'NO_TARGET',
  'UNKNOWN_SKILL',
]);

export const SkillCastResolvedPayloadSchema = z
  .object({
    playerSeatId: z.string().uuid(),
    skillId: SkillIdSchema,
    status: z.enum(['cast', 'rejected']),
    reason: SkillCastRejectReason.optional(),
    tickId: z.number().int().nonnegative(),
  })
  .strict()
  .refine((v) => v.status === 'cast' || v.reason !== undefined, {
    message: 'rejected status requires a reason',
  });

export const SkillCastResolvedTag = 'combat.skill_cast.resolved' as const;
export const SkillCastResolvedEnvelopeSchema = defineEnvelope(
  SkillCastResolvedTag,
  SkillCastResolvedPayloadSchema,
);
export type SkillCastResolvedPayload = z.infer<typeof SkillCastResolvedPayloadSchema>;

// ============ BROADCASTS ============

export const TelegraphDangerType = z.enum(['melee_swing', 'aoe_burst', 'projectile']);

export const TelegraphAreaSchema = z.discriminatedUnion('shape', [
  z
    .object({
      shape: z.literal('circle'),
      center: Vector2Schema,
      radius: z.number().positive(),
    })
    .strict(),
  z
    .object({
      shape: z.literal('cone'),
      origin: Vector2Schema,
      /** Dirección normalizada del cono. */
      forward: Vector2Schema,
      radius: z.number().positive(),
      /** Ángulo total del cono en grados. */
      angleDeg: z.number().positive().max(360),
    })
    .strict(),
]);

export type TelegraphArea = z.infer<typeof TelegraphAreaSchema>;

export const TelegraphStartedPayloadSchema = z
  .object({
    telegraphId: z.string(),
    sourceEntityId: z.string(),
    area: TelegraphAreaSchema,
    startTick: z.number().int().nonnegative(),
    resolveTick: z.number().int().nonnegative(),
    dangerType: TelegraphDangerType,
  })
  .strict()
  .refine((v) => v.resolveTick > v.startTick, {
    message: 'resolveTick must be > startTick',
  });

export const TelegraphStartedTag = 'combat.telegraph.started' as const;
export const TelegraphStartedEnvelopeSchema = defineEnvelope(
  TelegraphStartedTag,
  TelegraphStartedPayloadSchema,
);
export type TelegraphStartedPayload = z.infer<typeof TelegraphStartedPayloadSchema>;

export const TelegraphResolvedPayloadSchema = z
  .object({
    telegraphId: z.string(),
    hitEntities: z.array(z.string()),
    tickId: z.number().int().nonnegative(),
  })
  .strict();

export const TelegraphResolvedTag = 'combat.telegraph.resolved' as const;
export const TelegraphResolvedEnvelopeSchema = defineEnvelope(
  TelegraphResolvedTag,
  TelegraphResolvedPayloadSchema,
);
export type TelegraphResolvedPayload = z.infer<typeof TelegraphResolvedPayloadSchema>;

export const EntityRemovedPayloadSchema = z
  .object({
    entityId: z.string(),
    /** Causa informativa: muerte, despawn, etc. */
    reason: z.enum(['killed', 'despawn']),
    tickId: z.number().int().nonnegative(),
  })
  .strict();

export const EntityRemovedTag = 'entity.removed' as const;
export const EntityRemovedEnvelopeSchema = defineEnvelope(
  EntityRemovedTag,
  EntityRemovedPayloadSchema,
);
export type EntityRemovedPayload = z.infer<typeof EntityRemovedPayloadSchema>;
